import { useState, useEffect, useRef, useCallback } from 'react';
import { SearchIcon, Loader2Icon, MapPinIcon } from 'lucide-react';
import { useGetGeocodeSearch, getGetGeocodeSearchQueryKey, type GeocodeResult } from '@cdf/api-client-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface AddressSearchInputProps {
  value: string;
  onValueChange: (text: string) => void;
  onSelect: (result: { label: string; latitude: number; longitude: number }) => void;
  placeholder?: string;
  id?: string;
}

const DEBOUNCE_MS = 300;

/**
 * Debounced address autocomplete backed by the project's geocoding proxy.
 * Suggestions are biased to Victoria, Australia. Selecting a suggestion
 * fills the text and moves the map pin; free typing without selecting
 * still stores the raw text.
 */
export function AddressSearchInput({
  value,
  onValueChange,
  onSelect,
  placeholder = 'e.g. 123 Main Street, Port Melbourne',
  id,
}: AddressSearchInputProps) {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce the typed query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(value), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  const enabled = debouncedQuery.trim().length >= 3;

  const { data, isFetching, isError } = useGetGeocodeSearch(
    { q: debouncedQuery.trim() },
    {
      query: {
        queryKey: getGetGeocodeSearchQueryKey({ q: debouncedQuery.trim() }),
        enabled,
        staleTime: 60_000,
        retry: false,
      },
    },
  );

  const results: GeocodeResult[] = data?.results ?? [];

  // Open dropdown when results arrive or while fetching
  useEffect(() => {
    if (enabled && (isFetching || results.length > 0 || isError)) {
      setIsOpen(true);
      setActiveIndex(-1);
    }
  }, [enabled, isFetching, results, isError]);

  // Close on outside click
  useEffect(() => {
    function handleOutside(e: MouseEvent | TouchEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, []);

  const handleSelect = useCallback(
    (result: GeocodeResult) => {
      onValueChange(result.label);
      onSelect({ label: result.label, latitude: result.latitude, longitude: result.longitude });
      setIsOpen(false);
      setActiveIndex(-1);
      inputRef.current?.blur();
    },
    [onValueChange, onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || (results.length === 0 && !isFetching)) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < results.length) {
        e.preventDefault();
        handleSelect(results[activeIndex]!);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          id={id}
          className="pl-9"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (enabled && (results.length > 0 || isFetching || isError)) setIsOpen(true);
          }}
          autoComplete="off"
        />
        {isFetching && (
          <Loader2Icon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {isOpen && enabled && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-md">
          {isError && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Address search unavailable. Enter the address manually.
            </div>
          )}
          {!isError && !isFetching && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No matches found</div>
          )}
          {results.map((result, index) => (
            <button
              key={`${result.label}-${index}`}
              type="button"
              className={cn(
                'flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                index === activeIndex && 'bg-accent text-accent-foreground',
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(result);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="line-clamp-2">{result.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
